'use client';
import { type SitewidePromotionProps } from './_SitewidePromotion';
import { Anchor } from '@repo/ui/src/components/Anchor';
import PinIcon from '@repo/ui/src/components/Icons/PinIcon';
import { LinkProps } from '@repo/ui/src/types/link';
import { gsap } from 'gsap';
import React from 'react';
import dynamic from 'next/dynamic';
import LanguageSelector from '../LanguageSelector';
import Container from '@repo/ui/src/components/Container';
import { Region } from '@repo/ui/src/types/LanguageSelector';
import { classNames } from '@repo/ui/src/utils/classNames';
import { appConfig } from '../../utils/appConfig';

const SitewidePromotion = dynamic(() => import('./_SitewidePromotion'));

export interface SitewideBannerProps {
  dealerLocatorLink: LinkProps;
  sitewidePromotions: Array<SitewidePromotionProps>;
  regions: Array<Region>;
  getTZDate?: (dateString: string) => Date;
}

const STANDARD_ROTATION_TIME = 5;
const LONG_ROTATION_TIME = 8;

export const isActivePromo = (
  promo: Pick<SitewidePromotionProps, 'countdownClock' | 'link'>,
  getTZDate?: (dateString: string) => Date,
) => {
  if (
    !promo?.countdownClock ||
    !promo?.countdownClock?.endDate ||
    !promo?.countdownClock?.endTime
  ) {
    return true;
  }
  if (!promo?.link) {
    return false;
  }
  const { countdownClock } = promo;
  if (!getTZDate) return true;
  const start = getTZDate(`${countdownClock.startDate} ${countdownClock.startTime}`);
  const end = getTZDate(`${countdownClock.endDate} ${countdownClock.endTime}`);
  const now = new Date();
  return now > start && now < end;
};

const SitewideBanner = ({
  dealerLocatorLink,
  sitewidePromotions,
  regions,
  getTZDate,
}: SitewideBannerProps) => {
  const promotions = React.useMemo(
    () =>
      sitewidePromotions?.filter((promo) => isActivePromo(promo, getTZDate) && !promo.isDefault),
    [sitewidePromotions, getTZDate],
  );
  const defaultPromo = sitewidePromotions?.find((promo) => promo.isDefault);
  const rotator = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const t1 = gsap?.timeline({ repeat: -1 });

    if (!promotions || promotions.length === 1) {
      return;
    }

    promotions.forEach((promo, i) => {
      const time = promo.rotationTime === 'long' ? LONG_ROTATION_TIME : STANDARD_ROTATION_TIME;
      const target = `.slider-${i}`;
      if (i > 0) {
        gsap.set(target, { x: 100, opacity: 0, pointerEvents: 'none' });
        t1.to(target, { x: 0, opacity: 1, pointerEvents: 'all' }, '>');
      } else {
        t1.fromTo(target, { x: 100, opacity: 0 }, { x: 0, opacity: 1 }, '>');
      }
      t1.addLabel(target);
      t1.to(target, { x: -100, opacity: 0, pointerEvents: 'none', delay: time }, '>');
    });

    if (rotator.current) {
      rotator.current.addEventListener('mouseenter', () => {
        // Pause the rotation so someone can click it
        t1.pause();
      });

      rotator.current.addEventListener('mouseleave', () => {
        t1.play();
      });
    }

    return () => {
      t1.kill();
    };
  }, [promotions]);

  return (
    <section
      className={classNames(
        'hidden py-2 z-[1002] text-left relative items-center w-full max-w-screen sm:inline-block',
        appConfig.isTraeger && 'bg-[#25282A]',
        appConfig.isMeater && 'bg-[#c8102e]',
      )}
      aria-label={'Sitewide Banner for promotional and emergency messaging'}
      data-testid="site-wide-banner"
    >
      <Container className="flex justify-between items-center">
        <div className="grid grid-cols-[1fr_auto]">
          <div className="w-full overflow-hidden inline-block" ref={rotator}>
            {promotions?.length && (
              <ul className="m-0 p-0 relative flex flex-nowrap gap-x-2" role="presentation">
                {promotions.map((promo, i) => (
                  <SitewidePromotion key={i} {...promo} index={i} />
                ))}
                {!promotions.length && defaultPromo && <SitewidePromotion {...defaultPromo} />}
              </ul>
            )}
            {!promotions?.length && defaultPromo && <SitewidePromotion {...defaultPromo} />}
          </div>
        </div>
        <div className="hidden sm:flex flex-row gap-6">
          {dealerLocatorLink?.displayName && (
            <div className="grid gap-7 items-center">
              <Anchor
                {...dealerLocatorLink}
                className={classNames(
                  'group text-white flex gap-2 w-max items-center',
                  appConfig.isTraeger ? 'hover:!text-brand-200' : 'hover:!text-neutral-600',
                )}
              >
                <PinIcon className="transition-colors h-4" />
                <span
                  className={classNames(
                    'text-warm-100 text-xs leading-body w-full underline transition-colors',
                    appConfig.isTraeger
                      ? 'group-hover:!text-brand-200'
                      : 'group-hover:!text-neutral-600',
                  )}
                >
                  {dealerLocatorLink?.displayName}
                </span>
              </Anchor>
            </div>
          )}
          <LanguageSelector regions={regions} />
        </div>
      </Container>
    </section>
  );
};

export default SitewideBanner;
